// Turning a commodity's budget month -> contract month mapping into an actual contract
// date, and back into the twelve rows the settings table edits.
//
// The rule the mapping expresses: a budget month is not always priced against the same
// calendar month - corn budgeted for January is priced off the March contract, February
// off March as well, March off May. A commodity whose contract month always equals its
// budget month has no mapping at all, which is why every lookup here falls back to
// "contract month = budget month, same year" rather than to nothing.
import {
    CommodityContractMonthMappingModel,
    ContractYearOffset,
    UpdateCommodityContractMonthMappingModel,
} from '../Apis/ContractMonthMappingApi';
import { ContractDate } from '../Generated/Commodity-Management-Api';

export const MONTHS_IN_YEAR = 12;

/** Offsets in the order the settings dropdown lists them. */
export const CONTRACT_YEAR_OFFSETS: ContractYearOffset[] = [
    ContractYearOffset.Previous,
    ContractYearOffset.Current,
    ContractYearOffset.Next,
];

export const DEFAULT_CONTRACT_YEAR_OFFSET: ContractYearOffset = ContractYearOffset.Current;

export function getContractYearOffsetLabel(offset: ContractYearOffset): string {
    switch (offset) {
        case ContractYearOffset.Previous: return 'Previous';
        case ContractYearOffset.Next: return 'Next';
        default: return 'Current';
    }
}

/** Years to add to the budget year. The API sends names, not numbers. */
export function getContractYearDelta(offset: ContractYearOffset): number {
    switch (offset) {
        case ContractYearOffset.Previous: return -1;
        case ContractYearOffset.Next: return 1;
        default: return 0;
    }
}

/** One commodity's mapping, keyed by budget month. */
export type ContractMonthMappingByBudgetMonth = Record<number, CommodityContractMonthMappingModel>;

export function getMappingsForCommodity(
    mappings: readonly CommodityContractMonthMappingModel[] | undefined,
    commodityId: number | undefined,
): ContractMonthMappingByBudgetMonth {
    const byBudgetMonth: ContractMonthMappingByBudgetMonth = {};
    if (!mappings || commodityId === undefined) return byBudgetMonth;

    for (const mapping of mappings) {
        if (mapping.commodityId === commodityId) {
            byBudgetMonth[mapping.budgetMonth] = mapping;
        }
    }
    return byBudgetMonth;
}

export function commodityHasContractMonthMapping(
    mappings: readonly CommodityContractMonthMappingModel[] | undefined,
    commodityId: number | undefined,
): boolean {
    if (!mappings || commodityId === undefined) return false;
    return mappings.some(mapping => mapping.commodityId === commodityId);
}

/**
 * The contract date a budget month prices against. Falls back to the budget month and
 * year itself when the commodity has no row for that month - an unmapped commodity is
 * one whose contract month equals its budget month.
 *
 * ContractDate carries both parts as optional, so an incomplete budget date resolves to
 * nothing rather than to half a contract date.
 */
export function resolveContractDate(
    budgetMonth: number | undefined,
    budgetYear: number | undefined,
    mappingsByBudgetMonth: ContractMonthMappingByBudgetMonth,
): ContractDate | undefined {
    if (budgetMonth === undefined || budgetYear === undefined) return undefined;

    const mapping = mappingsByBudgetMonth[budgetMonth];

    if (!mapping) {
        return { month: budgetMonth, year: budgetYear };
    }

    return {
        month: mapping.contractMonth,
        year: budgetYear + getContractYearDelta(mapping.yearOffset),
    };
}

/** A budget month a contract month prices for. */
export interface BudgetMonthForContract {
    month: number;
    year: number;
}

/**
 * The budget months that price against a given contract date - the mapping read
 * backwards.
 *
 * Stored mappings run budget month -> contract month, which is the direction the settings
 * table edits and the direction a new budget needs. Splitting a futures position needs
 * the inverse: a March corn contract is bought to cover December, January and February
 * budget, so the position's volume belongs across those three months rather than sitting
 * entirely on March.
 *
 * The year runs backwards too. A budget month mapped to the NEXT year's contract (November
 * budget priced off next March) resolves from a March contract back to the November of the
 * PRECEDING year, so the delta is subtracted rather than added.
 *
 * A commodity with no mapping gets the identity - one entry, the contract date itself -
 * so an unmapped commodity behaves exactly as it did before any of this existed. A
 * commodity that IS mapped but has nothing pointing at this contract month gets an empty
 * list, which the caller should read as "cannot say" rather than "no months".
 */
export function getBudgetMonthsForContractDate(
    contractMonth: number | undefined,
    contractYear: number | undefined,
    mappingsByBudgetMonth: ContractMonthMappingByBudgetMonth,
): BudgetMonthForContract[] {
    if (contractMonth === undefined || contractYear === undefined) return [];

    const mappings = Object.values(mappingsByBudgetMonth);

    if (mappings.length === 0) {
        return [{ month: contractMonth, year: contractYear }];
    }

    return mappings
        .filter(mapping => mapping.contractMonth === contractMonth)
        .map(mapping => ({
            month: mapping.budgetMonth,
            year: contractYear - getContractYearDelta(mapping.yearOffset),
        }))
        .sort((a, b) => (a.year - b.year) || (a.month - b.month));
}

/** One editable row of the settings table. `contractMonth` null = nothing chosen yet. */
export interface ContractMonthMappingRow {
    budgetMonth: number;
    contractMonth: number | null;
    yearOffset: ContractYearOffset;
}

/**
 * Twelve rows, one per budget month, seeded from whatever is stored. A commodity with
 * no mapping starts on the identity mapping (January -> January, ...) so the table opens
 * on the behaviour the user already has rather than on twelve blank rows.
 */
export function buildContractMonthMappingRows(
    mappings: readonly CommodityContractMonthMappingModel[] | undefined,
    commodityId: number | undefined,
): ContractMonthMappingRow[] {
    const byBudgetMonth = getMappingsForCommodity(mappings, commodityId);
    const hasMapping = Object.keys(byBudgetMonth).length > 0;

    return Array.from({ length: MONTHS_IN_YEAR }, (_, index) => {
        const budgetMonth = index + 1;
        const mapping = byBudgetMonth[budgetMonth];

        if (mapping) {
            return {
                budgetMonth,
                contractMonth: mapping.contractMonth,
                yearOffset: mapping.yearOffset,
            };
        }

        return {
            budgetMonth,
            // A stored mapping that skips a month means that month is deliberately
            // unmapped; only a commodity with nothing stored gets the identity default.
            contractMonth: hasMapping ? null : budgetMonth,
            yearOffset: DEFAULT_CONTRACT_YEAR_OFFSET,
        };
    });
}

/** Rows to save. Rows with no contract month are dropped - they carry no mapping. */
export function toUpdateContractMonthMappings(
    rows: readonly ContractMonthMappingRow[],
): UpdateCommodityContractMonthMappingModel[] {
    return rows
        .filter(row => row.contractMonth !== null)
        .map(row => ({
            budgetMonth: row.budgetMonth,
            contractMonth: row.contractMonth!,
            yearOffset: row.yearOffset,
        }));
}

/** True when every row maps to its own month in the current year - i.e. no mapping needed. */
export function isIdentityMapping(rows: readonly ContractMonthMappingRow[]): boolean {
    return rows.every(row =>
        row.contractMonth === row.budgetMonth && row.yearOffset === ContractYearOffset.Current);
}
